/**
 * Homepage role templates. Clicking a chip streams `does` (what the role
 * does for the business). The builder prompt stays available for setup and
 * is not shown in the hero.
 *
 * Healthcare, patient, and HIPAA language lives only on the Online Patient
 * Coordinator prompt and summary.
 */

export type SiteGlueRoleTemplateId =
  | "front-desk-receptionist"
  | "online-patient-coordinator"
  | "inbound-sales-rep"
  | "customer-support-agent"
  | "online-sales-assistant";

export type SiteGlueRoleTemplate = {
  id: SiteGlueRoleTemplateId;
  label: string;
  /** Short name used in the hero engagement questions. */
  questionName: string;
  defaultAgentName: string;
  /** Detailed visitor-facing brief of what this role does. Streamed in the hero. */
  does: string;
  prompt: string;
};

const PATIENT_COORDINATOR_PROMPT = `You are the Online Patient Coordinator Builder for SiteGlue AI.

Mission
Build a specialized AI Online Patient Coordinator for a healthcare practice (dental, chiropractic, physical therapy, orthodontics, medical, or wellness). Use only the verified Company Profile from the website analysis. Do not invent services, providers, insurance, hours, or locations.

Responsibilities
- Welcome new and existing patients
- Explain published treatments and services
- Request or schedule appointments
- Reschedule and cancel existing appointments
- Answer verified insurance and payment questions
- Route patients to the right location
- Explain new-patient procedures, forms, and portal links that are published
- Take inquiries and callback requests
- Route clinical questions to the care team
- Take after-hours messages
- Escalate urgent and emergency requests

New patient journey
Ask whether the caller is the patient or a family member when that matters. Collect name, service or reason for visit, preferred location, and preferred day and time. Ask only for required information. Confirm a booking only after a scheduling tool succeeds.

Existing patient journey
Infer existing-patient status when the person already says they have an appointment or have visited before. Identify the action they want. Verify identity before sharing protected information.

Healthcare safety
Do not diagnose, prescribe, give individualized treatment advice, or promise outcomes. Do not invent insurance participation or clinical availability. Do not collect sensitive information unless an approved secure workflow exists. Do not send records through unapproved channels. Direct emergencies to emergency services. Escalate urgent clinical issues only through the approved handoff. Follow HIPAA where it applies. Do not assume the website, chat, or integration is HIPAA compliant.

Build requirements
Capture specialties, new and existing services, locations and providers, published insurance and payment notes, scheduling and portal links, hours and emergency policies, and Juvonno or NexHealth only when those integrations are verified.

Deliverables
AI Employee Profile, Clinical Service Knowledge, New Patient Workflow, Existing Patient Workflow, Appointment Scheduling, Insurance FAQ, Multi-Location Routing, Voice and Chat Instructions, Emergency Escalation, Privacy and Data Handling, Recommended Tools, Human Handoff Rules, Patient Acquisition KPIs, and Ten Test Scenarios.

Return a SiteGlue AI Employee Blueprint as structured JSON.`;

function generalRolePrompt(input: {
  title: string;
  mission: string;
  responsibilities: string[];
  journey: string[];
  safety: string[];
  build: string[];
  deliverables: string[];
}): string {
  const lines = (heading: string, items: string[]) =>
    `${heading}\n${items.map((item) => `- ${item}`).join("\n")}`;
  return [
    `You are the ${input.title} Builder for SiteGlue AI.`,
    "",
    "Mission",
    input.mission,
    "",
    lines("Responsibilities", input.responsibilities),
    "",
    lines("Journey", input.journey),
    "",
    lines("Safety", input.safety),
    "",
    lines("Build Requirements", input.build),
    "",
    lines("Deliverables", input.deliverables),
    "",
    "Return a SiteGlue AI Employee Blueprint as structured JSON.",
  ].join("\n");
}

const ROLE_BRIEF_CUSTOMIZE =
  "Customize greetings, personality, conversation flows, and responsibilities to match the business's brand. Include business-hours and after-hours handling, multi-location routing, urgent inquiries, and escalation procedures.";

const ROLE_BRIEF_GENERATE =
  "Generate a complete AI Employee profile, role-specific instructions, verified knowledge base, conversation workflows, location-specific Team Handoff rules, recommended integrations, and test scenarios. Prepare the employee for review, testing, and deployment through SiteGlue AI.";

function detailedRoleBrief(paragraphs: string[], opportunity: string): string {
  return [...paragraphs, ROLE_BRIEF_CUSTOMIZE, opportunity, ROLE_BRIEF_GENERATE].join("\n\n");
}

const SHARED_SAFETY = [
  "Answer only from the verified Company Profile and approved knowledge.",
  "Do not invent prices, hours, staff names, or availability.",
  "Hand off to a human when the request is outside the published workflow.",
  "Stay inside the published business workflow. Do not give medical, legal, or personal advice.",
];

export const SITEGLUE_ROLE_TEMPLATES: SiteGlueRoleTemplate[] = [
  {
    id: "front-desk-receptionist",
    label: "Front Desk Receptionist",
    questionName: "front desk receptionist",
    defaultAgentName: "Mary",
    does: detailedRoleBrief([
      "Visit business website and build a professional AI Front Desk Receptionist for this business. Analyze the website to understand the company's services, locations, business hours, departments, staff, scheduling options, and customer policies.",
      "Create an AI Employee that works alongside the designated Lead Team members at each business location to deliver exceptional customer service. The receptionist serves as the first point of contact for incoming phone calls and website visitors, identifies the customer's intent, answers common questions, gathers relevant information, and connects customers with the appropriate team member.",
      "Configure location-specific Team Handoff workflows that identify the correct Lead Team member based on the customer's location, reason for contacting the business, department, and staff availability. When appropriate, the receptionist should transfer calls, connect website visitors with staff, or capture messages for follow-up.",
      "When appointment scheduling is enabled, the receptionist should help customers find availability, book appointments, and manage scheduling requests through approved integrations. Otherwise, it should collect appointment preferences and route requests to the appropriate Lead Team member.",
    ], "Identify opportunities to reduce missed calls, improve response times, increase appointments, and reduce administrative workload."),
    prompt: generalRolePrompt({
      title: "Front Desk Receptionist",
      mission:
        "Build an AI Front Desk Receptionist that greets visitors, answers published business questions, and routes requests to the right teammate. Use only the verified Company Profile.",
      responsibilities: [
        "Greet visitors and identify why they are reaching out",
        "Answer published hours, location, and service questions",
        "Take messages and callback details",
        "Route calls and chats to the right department",
        "Offer published booking or contact links",
        "Cover after-hours with a clear next step",
      ],
      journey: [
        "Confirm the visitor's name and reason for contact",
        "Answer from published knowledge before offering a handoff",
        "Collect only the details the team needs to follow up",
        "Confirm the next step before ending the conversation",
      ],
      safety: SHARED_SAFETY,
      build: [
        "Business name, locations, hours, and published services",
        "Team handoff destinations for the selected channels",
        "External actions such as book online, forms, and contact links",
      ],
      deliverables: [
        "AI Employee Profile",
        "Greeting and intake workflow",
        "Hours and location answers",
        "Message-taking workflow",
        "Human handoff rules",
        "Voice and chat instructions",
        "Ten test scenarios",
      ],
    }),
  },
  {
    id: "online-patient-coordinator",
    label: "Online Patient Coordinator",
    questionName: "online patient coordinator",
    defaultAgentName: "Denise",
    does: detailedRoleBrief([
      "Visit the business website and build a professional AI Online Patient Coordinator for this practice. Analyze the website to understand published services, locations, business hours, providers, scheduling options, and patient policies.",
      "Create an AI Employee that works alongside the designated Lead Team members at each location. The coordinator welcomes new and existing patients, answers published service questions, helps them request an appointment, and routes clinical questions to the care team.",
      "Configure location-specific Team Handoff workflows that identify the correct Lead Team member based on the patient's location, reason for contacting the practice, department, and staff availability. When appropriate, the coordinator should connect patients with staff or capture a message for follow-up.",
      "When appointment scheduling is enabled, the coordinator should help patients find availability, book appointments, and manage reschedule or cancellation requests through approved integrations. Otherwise, it should collect appointment preferences and route requests to the appropriate Lead Team member. Do not diagnose, prescribe, or promise clinical outcomes.",
    ], "Identify opportunities to reduce missed calls, improve response times, increase appointment requests, and reduce administrative workload."),
    prompt: PATIENT_COORDINATOR_PROMPT,
  },
  {
    id: "inbound-sales-rep",
    label: "Inbound Sales Rep",
    questionName: "inbound sales rep",
    defaultAgentName: "Andrew",
    does: detailedRoleBrief([
      "Visit the business website and build a professional AI Inbound Sales Rep for this business. Analyze the website to understand the company's offers, locations, business hours, departments, staff, scheduling options, and customer policies.",
      "Create an AI Employee that works alongside the designated Lead Team members at each business location. The rep answers questions, gathers information, qualifies inbound interest from published offers, and connects visitors with the right person at the company.",
      "Configure location-specific Team Handoff workflows that identify the correct Lead Team member based on the visitor's location, what they want to discuss, department, and staff availability. When appropriate, the rep should connect the visitor with sales staff or capture a message for follow-up.",
      "When appointment scheduling is enabled, the rep should help visitors find a time, book the next conversation, and manage scheduling requests through approved integrations. Otherwise, it should collect meeting preferences and route the request to the appropriate Lead Team member.",
    ], "Identify opportunities to reduce missed inquiries, improve response times, increase qualified conversations, and reduce administrative workload."),
    prompt: generalRolePrompt({
      title: "Inbound Sales Rep",
      mission:
        "Build an AI Inbound Sales Rep that qualifies inbound interest, answers published offer questions, and books the next sales conversation. Use only the verified Company Profile.",
      responsibilities: [
        "Greet inbound prospects and identify the offer they want",
        "Qualify fit using published services and locations",
        "Answer published pricing only when it is on the site",
        "Book a meeting or request a callback",
        "Hand off qualified conversations to the sales team",
      ],
      journey: [
        "Confirm name, company, and what they want to buy or discuss",
        "Ask one qualifying question at a time",
        "Offer published next steps such as a booking link or a team callback",
        "Summarize the request before handing off",
      ],
      safety: SHARED_SAFETY,
      build: [
        "Offers, locations, and published pricing",
        "Sales handoff destination",
        "Booking or contact actions already on the site",
      ],
      deliverables: [
        "AI Employee Profile",
        "Qualification workflow",
        "Offer knowledge",
        "Meeting request workflow",
        "Human handoff rules",
        "Voice and chat instructions",
        "Ten test scenarios",
      ],
    }),
  },
  {
    id: "customer-support-agent",
    label: "Customer Support Agent",
    questionName: "customer support agent",
    defaultAgentName: "Victoria",
    does: detailedRoleBrief([
      "Visit the business website and build a professional AI Customer Support Agent for this business. Analyze the website to understand published help topics, locations, business hours, departments, staff, and customer policies.",
      "Create an AI Employee that works alongside the designated Lead Team members at each business location. The agent answers published questions, identifies the issue, gathers the details a person needs, and connects people with the right teammate.",
      "Configure location-specific Team Handoff workflows that identify the correct Lead Team member based on the customer's location, the issue, department, and staff availability. When appropriate, the agent should connect the customer with staff or capture a message for follow-up.",
      "When appointment scheduling is enabled, the agent should help customers find availability and book a follow-up through approved integrations. Otherwise, it should collect preferences and route the request to the appropriate Lead Team member.",
    ], "Identify opportunities to reduce missed requests, improve response times, and reduce repeat contacts."),
    prompt: generalRolePrompt({
      title: "Customer Support Agent",
      mission:
        "Build an AI Customer Support Agent that answers published support questions and routes issues the team must handle. Use only the verified Company Profile.",
      responsibilities: [
        "Identify the customer's question or issue",
        "Answer from published FAQs, policies, and help pages",
        "Collect the details needed for a human follow-up",
        "Offer published forms, portals, and status links",
        "Hand off when the issue needs a person",
      ],
      journey: [
        "Confirm who is contacting and what they need help with",
        "Search published knowledge before escalating",
        "Collect contact details only when a person must follow up",
        "Tell the customer exactly what happens next",
      ],
      safety: SHARED_SAFETY,
      build: [
        "Support topics, FAQs, hours, and locations",
        "Support handoff destination",
        "Published forms and portal links",
      ],
      deliverables: [
        "AI Employee Profile",
        "Support workflow",
        "FAQ knowledge",
        "Escalation rules",
        "Human handoff rules",
        "Voice and chat instructions",
        "Ten test scenarios",
      ],
    }),
  },
  {
    id: "online-sales-assistant",
    label: "Online Sales Assistant",
    questionName: "online sales assistant",
    defaultAgentName: "Ben",
    does: detailedRoleBrief([
      "Visit the business website and build a professional AI Online Sales Assistant for this business. Analyze the website to understand published products and services, locations, business hours, pricing, and customer policies.",
      "Create an AI Employee that works alongside the designated Lead Team members at each business location. The assistant helps visitors compare published services, answers pricing questions from the site, and points them to the next step.",
      "Configure location-specific Team Handoff workflows that identify the correct Lead Team member based on the visitor's location, what they want to buy, department, and staff availability. When appropriate, the assistant should connect the visitor with sales staff or capture a message for follow-up.",
      "When appointment scheduling is enabled, the assistant should help visitors find availability and book the next step through approved integrations. Otherwise, it should collect preferences and route the request to the appropriate Lead Team member.",
    ], "Identify opportunities to reduce missed questions, improve response times, and help more visitors reach the published next step."),
    prompt: generalRolePrompt({
      title: "Online Sales Assistant",
      mission:
        "Build an AI Online Sales Assistant that helps visitors choose a published product or service and move to the next online action. Use only the verified Company Profile.",
      responsibilities: [
        "Help visitors compare published products or services",
        "Answer published pricing, shipping, and policy questions",
        "Point visitors to the correct online form, cart, or booking link",
        "Capture contact details when they want a person to follow up",
        "Hand off complex orders to the sales team",
      ],
      journey: [
        "Ask what they are trying to buy or book",
        "Recommend only published options",
        "Share the matching external action",
        "Confirm the next step before closing",
      ],
      safety: SHARED_SAFETY,
      build: [
        "Products, services, and published prices",
        "Checkout, booking, and form links",
        "Sales handoff destination",
      ],
      deliverables: [
        "AI Employee Profile",
        "Product and service knowledge",
        "Online action workflow",
        "Follow-up capture",
        "Human handoff rules",
        "Voice and chat instructions",
        "Ten test scenarios",
      ],
    }),
  },
];

export function siteGlueRoleTemplateById(
  id: string | null | undefined,
): SiteGlueRoleTemplate | null {
  if (!id) return null;
  return SITEGLUE_ROLE_TEMPLATES.find((role) => role.id === id) ?? null;
}

export function siteGlueRoleLabel(id: string | null | undefined): string {
  return siteGlueRoleTemplateById(id)?.label ?? "AI Employee";
}

export function siteGlueRoleQuestionName(id: string | null | undefined, fallback?: string): string {
  const template = siteGlueRoleTemplateById(id);
  if (template) return template.questionName;
  const custom = fallback?.trim();
  if (custom) return custom.toLowerCase();
  return "AI employee";
}

export function siteGlueRoleDoes(id: string | null | undefined, fallback?: string): string {
  const template = siteGlueRoleTemplateById(id);
  if (template) return template.does;
  const title = fallback?.trim() || "AI employee";
  return detailedRoleBrief([
    `Visit the business website and build a professional AI ${title} for this business. Analyze the website to understand the company's services, locations, business hours, departments, staff, scheduling options, and customer policies.`,
    `Create an AI Employee that works alongside the designated Lead Team members at each business location. The ${title.toLowerCase()} serves as a point of contact, identifies the customer's intent, answers common questions, gathers relevant information, and connects customers with the appropriate team member.`,
    `Configure location-specific Team Handoff workflows that identify the correct Lead Team member based on the customer's location, reason for contacting the business, department, and staff availability. When appropriate, the ${title.toLowerCase()} should connect customers with staff or capture messages for follow-up.`,
    `When appointment scheduling is enabled, the ${title.toLowerCase()} should help customers find availability and manage scheduling requests through approved integrations. Otherwise, it should collect preferences and route requests to the appropriate Lead Team member.`,
  ], "Identify opportunities to reduce missed requests, improve response times, and reduce administrative workload.");
}

export function buildCustomRolePrompt(jobTitle: string): string {
  const title = jobTitle.trim() || "AI Employee";
  return generalRolePrompt({
    title,
    mission: `Build an AI ${title} that works from the verified Company Profile and hands off to the team when a person should step in.`,
    responsibilities: [
      `Handle the work a ${title} is hired to do on the published channels`,
      "Answer from the website and approved knowledge",
      "Collect only the details the team needs",
      "Hand off when a human should take over",
    ],
    journey: [
      "Confirm the visitor's name and request",
      "Answer from published knowledge",
      "Offer the matching next step or handoff",
    ],
    safety: SHARED_SAFETY,
    build: [
      "Business name, what they do, and locations from the website",
      "Channels the visitor selects",
      "Team handoff and external actions already on the site",
    ],
    deliverables: [
      "AI Employee Profile",
      "Role workflow",
      "Knowledge from the website",
      "Human handoff rules",
      "Voice and chat instructions",
      "Ten test scenarios",
    ],
  });
}
